import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Form,
    Input,
    Modal,
    Radio,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
    cancelTradingOrder,
    changeTradingCash,
    getTradingAccount,
    getTradingOrders,
    getTradingQuotes,
    modifyTradingOrder,
    placeTradingOrder,
} from '@/api/paperTrading'
import type {
    MarketSnapshot,
    PlaceTradingOrder,
    TradingAccount,
    TradingOrder,
} from '@/types/paperTrading'
import { useFeedback } from '@/utils/antdApp'

const currencyByMarket: Record<string, string> = { CN: 'CNY', HK: 'HKD', US: 'USD' }
/** 未完全成交、仍可改单/撤单的状态 */
const editableStatuses = new Set(['pending', 'accepted', 'partially_filled', 'price_adj_pending'])

type ModifyFormValues = {
    order_type: PlaceTradingOrder['order_type']
    price: string
    qty: string
}

function money(value: string | number | null | undefined) {
    if (value == null || Number.isNaN(Number(value))) return '--'
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function statusColor(status: string) {
    if (status === 'filled') return 'success'
    if (status === 'cancelled') return 'default'
    if (status === 'rejected') return 'error'
    if (status === 'partially_filled') return 'warning'
    return 'processing'
}

function fillStatusLabel(order: TradingOrder): { text: string; color: string } | null {
    const status = String(order.status || '').toLowerCase()
    const filledQty = Number(order.filled_qty)
    if (status === 'filled') {
        return { text: '全部成交', color: 'success' }
    }
    if (status === 'partially_filled' || (Number.isFinite(filledQty) && filledQty > 0)) {
        return { text: '部分成交', color: 'warning' }
    }
    if (editableStatuses.has(status)) {
        return { text: '未成交', color: 'default' }
    }
    return null
}

function canEditOrCancel(order: TradingOrder) {
    return editableStatuses.has(String(order.status || '').toLowerCase())
}

export default function TradingWorkspace({
    portfolioId,
    market,
}: {
    portfolioId: number
    market: string
}) {
    const { message, modal } = useFeedback()
    const [account, setAccount] = useState<TradingAccount | null>(null)
    const [orders, setOrders] = useState<TradingOrder[]>([])
    const [quote, setQuote] = useState<MarketSnapshot | null>(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [symbol, setSymbol] = useState(market === 'US' ? 'US.AAPL' : market === 'CN' ? 'SH.600519' : 'HK.00700')
    const [cashOpen, setCashOpen] = useState(false)
    const [cashAction, setCashAction] = useState<'deposit' | 'withdraw'>('deposit')
    const [modifyOpen, setModifyOpen] = useState(false)
    const [modifyTarget, setModifyTarget] = useState<TradingOrder | null>(null)
    const [modifying, setModifying] = useState(false)
    const [form] = Form.useForm<PlaceTradingOrder>()
    const [cashForm] = Form.useForm<{ amount: string }>()
    const [modifyForm] = Form.useForm<ModifyFormValues>()

    const refresh = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true)
        try {
            const [accountData, orderData] = await Promise.all([
                getTradingAccount(portfolioId),
                getTradingOrders(portfolioId, { page: 1, size: 100 }),
            ])
            setAccount(accountData)
            setOrders(orderData.items ?? [])
        } catch (err) {
            if (!quiet) message.error(err instanceof Error ? err.message : '加载模拟交易账户失败')
        } finally {
            if (!quiet) setLoading(false)
        }
    }, [portfolioId, message])

    useEffect(() => {
        void refresh()
        const timer = window.setInterval(() => void refresh(true), 10_000)
        return () => window.clearInterval(timer)
    }, [refresh])

    const loadQuote = async () => {
        try {
            const normalized = symbol.trim().toUpperCase()
            const data = await getTradingQuotes(portfolioId, [normalized])
            const next = data.items[0]
            if (!next) throw new Error('未查询到该标的行情')
            setSymbol(normalized)
            setQuote(next)
            form.setFieldValue('symbol', normalized)
            if (form.getFieldValue('order_type') !== 'market') {
                form.setFieldValue('price', String(next.last_price))
            }
        } catch (err) {
            message.error(err instanceof Error ? err.message : '行情查询失败')
        }
    }

    const submitOrder = async (values: PlaceTradingOrder) => {
        setSubmitting(true)
        try {
            await placeTradingOrder(portfolioId, { ...values, symbol: values.symbol.trim().toUpperCase() })
            message.success('订单已提交')
            await refresh(true)
        } catch (err) {
            message.error(err instanceof Error ? err.message : '下单失败')
        } finally {
            setSubmitting(false)
        }
    }

    const confirmCancelOrder = (order: TradingOrder) => {
        modal.confirm({
            title: '确认撤单',
            content: (
                <div>
                    确认撤销 {order.symbol} {order.side === 'buy' ? '买入' : '卖出'} 订单？
                    <div style={{ marginTop: 8, color: 'var(--text-3)' }}>
                        委托 {order.qty} / 已成交 {order.filled_qty}
                        {order.order_type === 'limit' ? `，委托价 ${order.price}` : '，市价'}
                    </div>
                </div>
            ),
            okText: '确认撤单',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: async () => {
                try {
                    await cancelTradingOrder(portfolioId, order.id)
                    message.success('撤单请求已提交')
                    await refresh(true)
                } catch (err) {
                    message.error(err instanceof Error ? err.message : '撤单失败')
                    throw err
                }
            },
        })
    }

    const openModify = (order: TradingOrder) => {
        const remaining = Math.max(0, Number(order.qty) - Number(order.filled_qty))
        setModifyTarget(order)
        modifyForm.setFieldsValue({
            order_type: order.order_type,
            price: order.order_type === 'market' ? '' : order.price,
            qty: String(remaining > 0 ? remaining : order.qty),
        })
        setModifyOpen(true)
    }

    const submitModify = async (values: ModifyFormValues) => {
        if (!modifyTarget) return
        const payload: PlaceTradingOrder = {
            symbol: modifyTarget.symbol,
            side: modifyTarget.side,
            order_type: values.order_type,
            price: values.order_type === 'market' ? '0' : values.price,
            qty: values.qty,
            time_in_force: modifyTarget.time_in_force,
        }
        modal.confirm({
            title: '确认改单',
            content: (
                <div>
                    可调整订单类型、价格与数量；标的与方向不可变更。
                    <div style={{ marginTop: 8, color: 'var(--text-3)' }}>
                        {modifyTarget.symbol} {modifyTarget.side === 'buy' ? '买入' : '卖出'}
                        {' · '}
                        {values.order_type === 'market' ? '市价' : `限价 ${values.price}`}
                        {' · '}数量 {values.qty}
                    </div>
                </div>
            ),
            okText: '确认改单',
            cancelText: '取消',
            onOk: async () => {
                setModifying(true)
                try {
                    await modifyTradingOrder(portfolioId, modifyTarget.id, payload)
                    message.success('改单已提交')
                    setModifyOpen(false)
                    setModifyTarget(null)
                    modifyForm.resetFields()
                    await refresh(true)
                } catch (err) {
                    message.error(err instanceof Error ? err.message : '改单失败')
                    throw err
                } finally {
                    setModifying(false)
                }
            },
        })
    }

    const submitCash = async ({ amount }: { amount: string }) => {
        try {
            await changeTradingCash(
                portfolioId,
                cashAction,
                currencyByMarket[market] ?? 'HKD',
                amount,
            )
            message.success(cashAction === 'deposit' ? '入金成功' : '出金成功')
            setCashOpen(false)
            cashForm.resetFields()
            await refresh(true)
        } catch (err) {
            message.error(err instanceof Error ? err.message : '资金操作失败')
        }
    }

    const orderColumns: ColumnsType<TradingOrder> = useMemo(() => [
        { title: '时间', dataIndex: 'created_at', width: 170, render: v => v ? new Date(v).toLocaleString() : '--' },
        {
            title: '标的', dataIndex: 'symbol',
            render: (v, row) => {
                const name = (row.name || row.stock_name || '').trim() || v
                return (
                    <Space>
                        <div>
                            <strong>{name}</strong>
                            {v && name !== v && (
                                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{v}</div>
                            )}
                        </div>
                        <Tag color={row.side === 'buy' ? 'green' : 'red'}>{row.side === 'buy' ? '买入' : '卖出'}</Tag>
                    </Space>
                )
            },
        },
        { title: '类型', render: (_, row) => `${row.order_type === 'market' ? '市价' : '限价'} / ${row.time_in_force}` },
        { title: '委托价', dataIndex: 'price', align: 'right', render: (v, row) => row.order_type === 'market' ? '市价' : money(v) },
        { title: '委托 / 成交', align: 'right', render: (_, row) => `${row.qty} / ${row.filled_qty}` },
        { title: '成交均价', dataIndex: 'avg_fill_price', align: 'right', render: money },
        { title: '状态', dataIndex: 'status', render: v => <Tag color={statusColor(v)}>{v}</Tag> },
        {
            title: '操作', key: 'actions', width: 200,
            render: (_, row) => {
                const fillLabel = fillStatusLabel(row)
                const editable = canEditOrCancel(row)
                return (
                    <Space size={4} wrap>
                        {fillLabel && (
                            <Tag color={fillLabel.color} style={{ marginInlineEnd: 0, cursor: 'default' }}>
                                {fillLabel.text}
                            </Tag>
                        )}
                        {editable && (
                            <>
                                <Button type="link" size="small" onClick={() => openModify(row)}>改单</Button>
                                <Button danger type="link" size="small" onClick={() => confirmCancelOrder(row)}>撤单</Button>
                            </>
                        )}
                    </Space>
                )
            },
        },
    ], [portfolioId, modal, message])

    if (!account && !loading) {
        return <Alert type="warning" showIcon message="模拟交易账户不可用" description="请确认该组合已开通模拟盘，并检查 market-module 服务配置。" />
    }

    const primaryCash = account?.cash.find(item => item.currency === currencyByMarket[market])

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={12}>
                <Col span={6}><Card loading={loading}><Statistic title="可用资金" value={Number(primaryCash?.available ?? 0)} precision={2} suffix={primaryCash?.currency} /></Card></Col>
                <Col span={6}><Card loading={loading}><Statistic title="冻结资金" value={Number(primaryCash?.frozen ?? 0)} precision={2} suffix={primaryCash?.currency} /></Card></Col>
                <Col span={6}><Card loading={loading}><Statistic title="在途资金" value={Number(primaryCash?.in_transit ?? 0)} precision={2} suffix={primaryCash?.currency} /></Card></Col>
                <Col span={6}>
                    <Card loading={loading}>
                        <Space direction="vertical">
                            <span style={{ color: 'var(--text-3)' }}>资金操作</span>
                            <Space>
                                <Button size="small" onClick={() => { setCashAction('deposit'); setCashOpen(true) }}>入金</Button>
                                <Button size="small" onClick={() => { setCashAction('withdraw'); setCashOpen(true) }}>出金</Button>
                                <Button size="small" onClick={() => void refresh()}>刷新</Button>
                            </Space>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={9}>
                    <Card title="实时行情与下单">
                        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                            <Input value={symbol} onChange={e => setSymbol(e.target.value)} onPressEnter={() => void loadQuote()} />
                            <Button type="primary" onClick={() => void loadQuote()}>查询</Button>
                        </Space.Compact>
                        {quote && (
                            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
                                <Descriptions.Item label="名称">{quote.name}</Descriptions.Item>
                                <Descriptions.Item label="状态">{quote.suspension ? <Tag color="red">停牌</Tag> : quote.sec_status}</Descriptions.Item>
                                <Descriptions.Item label="最新价"><strong>{money(quote.last_price)}</strong></Descriptions.Item>
                                <Descriptions.Item label="涨跌">
                                    <span style={{ color: quote.last_price >= quote.prev_close_price ? '#ef4444' : '#22c55e' }}>
                                        {quote.prev_close_price ? `${((quote.last_price / quote.prev_close_price - 1) * 100).toFixed(2)}%` : '--'}
                                    </span>
                                </Descriptions.Item>
                                <Descriptions.Item label="买一">{money(quote.bid_price)} / {quote.bid_vol}</Descriptions.Item>
                                <Descriptions.Item label="卖一">{money(quote.ask_price)} / {quote.ask_vol}</Descriptions.Item>
                                <Descriptions.Item label="每手">{quote.lot_size || 1}</Descriptions.Item>
                                <Descriptions.Item label="成交量">{quote.volume?.toLocaleString()}</Descriptions.Item>
                            </Descriptions>
                        )}
                        <Form<PlaceTradingOrder>
                            form={form}
                            layout="vertical"
                            initialValues={{ symbol, side: 'buy', order_type: 'limit', price: '', qty: '', time_in_force: 'DAY' }}
                            onFinish={submitOrder}
                        >
                            <Form.Item name="symbol" label="标的代码" rules={[{ required: true }]}><Input /></Form.Item>
                            <Form.Item name="side" label="方向"><Radio.Group block options={[{ label: '买入', value: 'buy' }, { label: '卖出', value: 'sell' }]} optionType="button" buttonStyle="solid" /></Form.Item>
                            <Row gutter={12}>
                                <Col span={12}><Form.Item name="order_type" label="订单类型"><Select options={[{ label: '限价单', value: 'limit' }, { label: '市价单', value: 'market' }]} /></Form.Item></Col>
                                <Col span={12}><Form.Item name="time_in_force" label="有效期"><Select options={['DAY', 'IOC', 'FOK', 'GTC'].map(v => ({ label: v, value: v }))} /></Form.Item></Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={12}><Form.Item noStyle shouldUpdate>{({ getFieldValue }) => getFieldValue('order_type') === 'market' ? null : <Form.Item name="price" label="委托价" rules={[{ required: true }]}><Input inputMode="decimal" /></Form.Item>}</Form.Item></Col>
                                <Col span={12}><Form.Item name="qty" label="数量" rules={[{ required: true }]}><Input inputMode="numeric" /></Form.Item></Col>
                            </Row>
                            <Button type="primary" htmlType="submit" block loading={submitting}>提交订单</Button>
                        </Form>
                    </Card>
                </Col>
                <Col span={15}>
                    <Card title="订单记录" extra={<span style={{ color: 'var(--text-3)' }}>每 10 秒刷新</span>}>
                        <Table rowKey="id" size="small" loading={loading} columns={orderColumns} dataSource={orders} scroll={{ x: 960 }} pagination={{ pageSize: 8 }} />
                    </Card>
                </Col>
            </Row>

            <Modal
                title={cashAction === 'deposit' ? '模拟盘入金' : '模拟盘出金'}
                open={cashOpen}
                onCancel={() => setCashOpen(false)}
                onOk={() => cashForm.submit()}
            >
                <Form form={cashForm} layout="vertical" onFinish={submitCash}>
                    <Form.Item label="币种"><Input disabled value={currencyByMarket[market] ?? 'HKD'} /></Form.Item>
                    <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
                        <Input inputMode="decimal" placeholder="请输入正数金额" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="改单"
                open={modifyOpen}
                onCancel={() => {
                    setModifyOpen(false)
                    setModifyTarget(null)
                    modifyForm.resetFields()
                }}
                onOk={() => modifyForm.submit()}
                confirmLoading={modifying}
                okText="下一步"
                destroyOnClose
            >
                {modifyTarget && (
                    <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="标的">{modifyTarget.symbol}</Descriptions.Item>
                        <Descriptions.Item label="方向">{modifyTarget.side === 'buy' ? '买入' : '卖出'}</Descriptions.Item>
                        <Descriptions.Item label="有效期">{modifyTarget.time_in_force}</Descriptions.Item>
                        <Descriptions.Item label="原委托 / 已成交">{modifyTarget.qty} / {modifyTarget.filled_qty}</Descriptions.Item>
                    </Descriptions>
                )}
                <Form<ModifyFormValues> form={modifyForm} layout="vertical" onFinish={submitModify}>
                    <Form.Item name="order_type" label="订单类型" rules={[{ required: true }]}>
                        <Select options={[{ label: '限价', value: 'limit' }, { label: '市价', value: 'market' }]} />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) =>
                            getFieldValue('order_type') === 'market' ? null : (
                                <Form.Item name="price" label="委托价" rules={[{ required: true, message: '请输入委托价' }]}>
                                    <Input inputMode="decimal" />
                                </Form.Item>
                            )
                        }
                    </Form.Item>
                    <Form.Item
                        name="qty"
                        label="数量"
                        extra="默认填入剩余未成交数量；改单会撤旧单并按此数量下新单"
                        rules={[{ required: true, message: '请输入数量' }]}
                    >
                        <Input inputMode="numeric" />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    )
}
